// src/components/SchoolDetails.js
import React from 'react';
import { 
  calculateExpectedPublicationDays,
  getSchoolTimeliness,
  hasTimelinessProblems,
  getSchoolCompliance,
  isDateOutdatedTmRk,
  isDateOutdatedcp,
  THRESHOLDS
} from '../utils/calculations';

const SchoolDetails = ({ school, schoolData, filters }) => {
  return (
    <div className="school-details">
      <DetailSection title="Даты документов" icon="📅">
        <SchoolDates school={school} />
      </DetailSection>

      <DetailSection title="Ошибки в меню" icon="❌">
        <SchoolErrors errors={schoolData.errors} />
      </DetailSection>

      

      <DetailSection title="Соответствие меню" icon="⚖️">
        <SchoolCompliance compliance={schoolData.compliance} />
      </DetailSection>

      <DetailSection title="Своевременность" icon="⏱️">
        <SchoolTimeliness 
          school={school} 
          timelinessData={schoolData.timeliness || []}
        />
      </DetailSection>
    </div>
  );
};

const DetailSection = ({ title, icon, children }) => (
  <div className="detail-section">
    <h4><span className="section-icon">{icon}</span> {title}</h4>
    {children}
  </div>
);

const SchoolDates = ({ school }) => {
  const tmStatus = isDateOutdatedTmRk(school.ДатаТМ) ? 'date-old' : 'date-ok';
  const kpStatus = isDateOutdatedcp(school.ДатаКП) ? 'date-old' : 'date-ok';
  const rkStatus = isDateOutdatedTmRk(school.ДатаРК) ? 'date-old' : 'date-ok';

  return (
    <table>
      <thead>
        <tr><th>Тип документа</th><th>Дата</th><th>Статус</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Типовое меню</strong></td>
          <td><span className={tmStatus}>{school.ДатаТМ || 'нет данных'}</span></td>
          <td>{isDateOutdatedTmRk(school.ДатаТМ) ? 
            <span className="error">Требует обновления</span> : 
            <span className="success">Актуально</span>}
          </td>
        </tr>
        <tr>
          <td><strong>Календарь питания</strong></td>
          <td><span className={kpStatus}>{school.ДатаКП || 'нет данных'}</span></td>
          <td>{isDateOutdatedcp(school.ДатаКП) ? 
            <span className="error">Требует обновления</span> : 
            <span className="success">Актуально</span>}
          </td>
        </tr>
        <tr>
          <td><strong>Родительский контроль</strong></td>
          <td><span className={rkStatus}>{school.ДатаРК || 'нет данных'}</span></td>
          <td>{isDateOutdatedTmRk(school.ДатаРК) ? 
            <span className="error">Требует обновления</span> : 
            <span className="success">Актуально</span>}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const SchoolErrors = ({ errors }) => {
  if (errors.length === 0) {
    return <div className="success-message"><p className="success">Ошибок в меню не обнаружено</p></div>;
  }

  return (
    <div className="errors-container">
      <p className="error">Обнаружено {errors.length} ошибок в меню</p>
      <table>
        <thead><tr><th>Дата</th><th>Прием пищи</th><th>Ошибка</th></tr></thead>
        <tbody>
          {errors.map((item, index) => (
            <tr key={index}>
              <td>{item.Дата}</td>
              <td>{item.ПриемПищи}</td>
              <td className="error-text">{item.Ошибка}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SchoolPublication = ({ school, publication, month, year }) => {
  const publicationRecords = publication.filter(item => item.Код.toString() === school.Код.toString());
  
  if (publicationRecords.length === 0) {
    return <div className="publication-container"><p className="error">Меню не публикуется</p></div>;
  }

  const publicationDays = [...new Set(publicationRecords.map(item => item.Дата))];
  const publicationCount = publicationDays.length;
  const expectedPublicationDays = calculateExpectedPublicationDays(month, year, true);
  
  const goodThreshold = Math.floor(expectedPublicationDays * THRESHOLDS.PUBLICATION_PERCENT);
  const warningThreshold = Math.floor(expectedPublicationDays * 0.5);
  const percentage = expectedPublicationDays > 0 ? Math.round((publicationCount / expectedPublicationDays) * 100) : 0;

  let statusClass = 'publication-good';
  let statusText = 'Норма';

  if (publicationCount === 0) {
    statusClass = 'publication-bad';
    statusText = 'Не публикует';
  } else if (publicationCount < warningThreshold) {
    statusClass = 'publication-warning';
    statusText = 'Мало публикаций';
  } else if (publicationCount < goodThreshold) {
    statusClass = 'publication-warning';
    statusText = 'Ниже нормы';
  }

  return (
    <div className="publication-container">
      <div className="publication-stats">
        <p><strong>Дней с публикацией:</strong> {publicationCount}</p>
        <p><strong>Ожидаемых дней:</strong> {expectedPublicationDays}</p>
        <p><strong>Процент публикации:</strong> <span className={statusClass}>{percentage}%</span></p>
        <p><strong>Статус:</strong> <span className={statusClass}>{statusText}</span></p>
      </div>
    </div>
  );
};

const SchoolCompliance = ({ compliance }) => {
  if (compliance.length === 0) {
    return <div className="compliance-container"><p className="no-data">Нет данных о соответствии меню</p></div>;
  }

  const avgCompliance = compliance.reduce((sum, item) => sum + item.ПроцентСоблюдения, 0) / compliance.length;
  const totalViolations = compliance.reduce((sum, item) => sum + item.ЧислоНесоблюдений, 0);

  return (
    <div className="compliance-container">
      <div className="compliance-summary">
        <p><strong>Среднее соответствие:</strong> 
          <span className={avgCompliance >= 90 ? 'success' : avgCompliance >= 70 ? 'warning' : 'error'}>
            {avgCompliance.toFixed(1)}%
          </span>
        </p>
        <p><strong>Всего несоблюдений:</strong> {totalViolations}</p>
        <p><strong>Количество проверок:</strong> {compliance.length}</p>
      </div>
      
      <table>
        <thead><tr><th>Дата</th><th>Несоблюдений</th><th>% соблюдения</th><th>Статус</th></tr></thead>
        <tbody>
          {compliance.map((item, index) => {
            const complianceClass = item.ПроцентСоблюдения > 90 ? 'success' : 
                                  item.ПроцентСоблюдения > 70 ? 'warning' : 'error';
            const status = item.ПроцентСоблюдения > 90 ? 'Высокий' : 
                          item.ПроцентСоблюдения > 70 ? 'Средний' : 'Низкий';
            return (
              <tr key={index}>
                <td>{item.Дата}</td>
                <td>{item.ЧислоНесоблюдений}</td>
                <td className={complianceClass}>{item.ПроцентСоблюдения}%</td>
                <td><span className={complianceClass}>{status}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SchoolTimeliness = ({ school, timelinessData }) => {
  const timelinessPercentage = getSchoolTimeliness(school, timelinessData);
  const hasProblems = hasTimelinessProblems(school, timelinessData, THRESHOLDS.TIMELINESS_GOOD);
  
  if (timelinessPercentage === null) {
    return <div className="timeliness-container"><p className="no-data">Нет данных о своевременности</p></div>;
  }
  
  return (
    <div className="timeliness-container">
      <div className="timeliness-stats">
        <p><strong>Процент своевременности:</strong> 
          <span className={hasProblems ? 'error' : 'success'} style={{ fontSize: '18px', marginLeft: '10px' }}>
            {timelinessPercentage}%
          </span>
        </p>
        <p><strong>Статус:</strong> 
          <span className={hasProblems ? 'error' : 'success'} style={{ marginLeft: '10px' }}>
            {hasProblems ? 'Требуется улучшение' : 'Хороший показатель'}
          </span>
        </p>
      </div>
    </div>
  );
};

export default SchoolDetails;